import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, showToast } from '@mercai/clever'
import { OrderCenterForm, OrderCenterShow } from '@/components'
import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { useParamsState } from '@/helpers'
import { OrderCenterTable } from '@/pages/order-center/components'
import {
  QUERIES_ADMIN,
  useBulkSaleOrder,
  useOrderCenterSaleOrderList,
} from '@/services'
import { useQueryClient } from '@tanstack/react-query'
import { SaleOrderProps } from '@/types'

import { ProfileShowOrderProps } from './profile-order.type'
import { ModalBulkKam } from '../modal-bulk-kam'

export const ProfileShowOrder = ({
  buyerProfileUuid,
  isLoadingKams = false,
  kams = [],
  setSearchKams,
}: ProfileShowOrderProps) => {
  const { t } = useTranslation('screenProfileShow')

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    search: '',
    taxStatus: '',
    moscow: '',
    createdAtBefore: '',
    createdAtAfter: '',
    ordering: '',
    keyAccountManager: '',
  })

  const { data: dataSaleOrder, isLoading: isLoadginSaleOrder } =
    useOrderCenterSaleOrderList(
      { ...filter, buyer_profile: buyerProfileUuid },
      true,
    )

  const [queryParams, setQueryParams] = useParamsState({
    uuid: '',
    action: '',
  })

  const defaultProps = {
    filter: {
      page: filter.page,
      limit: filter.limit,
      search: filter.search,
      taxStatus: filter.taxStatus,
      moscow: filter.moscow,
      createdAtBefore: filter.createdAtBefore,
      createdAtAfter: filter.createdAtAfter,
    },
    onEditRegister: (uuid: string) =>
      setQueryParams({
        uuid,
        action: 'form',
      }),
    onSetFilter: setFilter,
    onShowRegister: (uuid: string) =>
      setQueryParams({
        uuid,
        action: 'show',
      }),
    formattedFilter: {},
    onDeleteRegister: () => {},
  }

  const queryClient = useQueryClient()

  const [isOpenModalBulkKam, setisOpenModalBulkKam] = useState(false)

  const [saleOrderChecked, setSaleOrderChecked] = useState<string[]>([])

  const [selectedKam, setSelectedKam] = useState<{
    label: string
    value: string
  }>()

  const kamsOptions = useMemo(() => {
    const kamsWithName = kams?.filter((user) => user?.name) || []

    const mappedKams = kamsWithName.map((user) => ({
      label: user.name,
      value: `${user.id}`,
    }))

    const options = [...mappedKams]

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [kams])

  const { mutateAsync: bulkSaleOrderKam, isPending: isLoadingBulk } =
    useBulkSaleOrder()

  const onBulkSaleOrderKAM = async () => {
    const response = await bulkSaleOrderKam({
      data: {
        keyAccountManagerId: selectedKam?.value
          ? +selectedKam?.value
          : undefined,
      } as Partial<SaleOrderProps>,
      items: saleOrderChecked,
    })

    if (!response.success) {
      showToast({
        variant: 'error',
        title: t('bulkKAMError'),
      })
      return
    }

    setSaleOrderChecked([])
    setisOpenModalBulkKam(false)
    setSelectedKam(undefined)

    showToast({
      variant: 'success',
      title: t('bulkKAMSuccess'),
    })

    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.ORDER_CENTER.module],
    })
  }

  const BulkSaleOrderKAM = () => {
    return (
      <Button
        variant="primary"
        onClick={() => setisOpenModalBulkKam(true)}
        disabled={saleOrderChecked.length === 0}
      >
        {t('bulkKAMButton')}
      </Button>
    )
  }

  const onCloseModalBulkKam = () => {
    setisOpenModalBulkKam(false)
    setSelectedKam(undefined)
  }

  return (
    <>
      <OrderCenterTable
        {...defaultProps}
        data={dataSaleOrder?.data}
        isLoading={isLoadginSaleOrder}
        type="all"
        setCheckedRows={setSaleOrderChecked}
        checkedRows={saleOrderChecked}
        {...(saleOrderChecked.length > 0 && {
          headerComponents: <BulkSaleOrderKAM />,
        })}
      />

      <OrderCenterShow
        isOpen={queryParams.action === 'show'}
        onHandleOpen={() =>
          setQueryParams({ ...queryParams, action: '', uuid: '' })
        }
        saleOrderUuid={queryParams.uuid}
        type={'all'}
      />

      <OrderCenterForm
        isOpen={queryParams.action === 'form'}
        onHandleOpen={() =>
          setQueryParams({ ...queryParams, action: '', uuid: '' })
        }
        saleOrderUuid={queryParams.uuid}
        type={'all'}
      />

      {isOpenModalBulkKam && (
        <ModalBulkKam
          onClose={onCloseModalBulkKam}
          isOpen={isOpenModalBulkKam}
          kams={kamsOptions}
          isLoadingKams={isLoadingKams}
          setSearchKams={setSearchKams}
          selectedKam={selectedKam}
          setSelectedKam={setSelectedKam}
          onSubmit={onBulkSaleOrderKAM}
          isLoadingSubmit={isLoadingBulk}
        />
      )}
    </>
  )
}
